import {Power3, Timeline, TimelineLite, TweenLite} from "gsap";
import {
    IBitmapCountUpConfig,
    ICountUpAnchor,
    ICountUpAnimationConfig
} from "@nolimitcity/slot-game/bin/core/component/BitmapCountUp";
import {ICurrencyValueFormatOptions} from "@nolimitcity/slot-game/bin/core/balance/Currency";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {BonusBunniesSoundConfig} from "../../../BonusBunniesSoundConfig";

/**
 * Created by Jerker Nord on 2020-05-07.
 */

export class BonusBunniesBitmapCountUp extends PIXI.BitmapText {

    private _numberFormat:ICurrencyValueFormatOptions | undefined;
    private _anchors:ICountUpAnchor[];
    private _externalOnUpdateCallbacks:(() => void)[];
    private _onWidthChangeCallback:(() => void) | undefined;

    private _highestTriggeredAnchorIndex:number = -1;
    private _isCountingUp:boolean = false;
    private _scaling:number = 1;
    private _currentValue:number;

    private _originalFontSize:number;
    private _shrinkSize:boolean;
    private _maxTextWidth:number;
    private _upgradeSound:string[] = [BonusBunniesSoundConfig.instance.BONUS_MULTIPLIER_UPGRADE2,
        BonusBunniesSoundConfig.instance.BONUS_MULTIPLIER_UPGRADE3, BonusBunniesSoundConfig.instance.BONUS_MULTIPLIER_UPGRADE4,
        BonusBunniesSoundConfig.instance.BONUS_MULTIPLIER_UPGRADE5, BonusBunniesSoundConfig.instance.BONUS_MULTIPLIER_UPGRADE6];
    private _currentUpgradeSound:number = 0;

    private _isResizeDirty:boolean = false;
    private _playSound:boolean;

    get shrinkSize():boolean {
        return this._shrinkSize;
    }

    set shrinkSize(value:boolean) {
        this._shrinkSize = value;
    }

    get maxTextWidth():number {
        return this._maxTextWidth;
    }

    set maxTextWidth(value:number) {
        this._maxTextWidth = value;
        if(this.parent) {
            this.updateCountUpTextPivot();
        } else {
            this._isResizeDirty = true;
        }
    }

    get currentValue():number {
        return this._currentValue;
    }

    set currentValue(value:number) {
        this._currentValue = value;
    }

    get scaling():number {
        return this._scaling;
    }

    set scaling(value:number) {
        this._scaling = value;
    }

    constructor(config:IBitmapCountUpConfig) {
        super("0", {
            fontName:config.style.font.name!,
            fontSize:config.style.font.size
        });

        this._anchors = [];
        this._externalOnUpdateCallbacks = [];
        this._onWidthChangeCallback = config.onWidthChangeCallback;
        this._numberFormat = config.numberFormat;
        this._originalFontSize = (<{ name:string, size:number }>config.style.font).size;

        this.on("added", this.onAdded);
    }

    private onAdded():void {
        if(this._isResizeDirty) {
            this.updateCountUpTextPivot();
        }
    }

    public addAnchor(anchor:ICountUpAnchor):void {
        if(this._isCountingUp) {
            debugger;
            throw new Error("Error: BitmapCountUp.addAnchor():Not allow to edit anchors while counting up!");
        }
        this._anchors.push(anchor);
        this._anchors.sort((a:ICountUpAnchor, b:ICountUpAnchor):number => {return a.value - b.value});
    }

    public clearAllAnchors():void {
        if(this._isCountingUp) {
            debugger;
            throw new Error("Error: BitmapCountUp.clearAllAnchors():Not allow to edit anchors while counting up!");
        }
        this._anchors = [];
    }

    public addExternalOnUpdateCallback(callback:() => void):void {
        this._externalOnUpdateCallbacks.push(callback);
    }

    public getCountUpAnimation(playSound:boolean, fromValue:number, toValue:number, animationConfig:ICountUpAnimationConfig, onCompleteCallback?:() => void):Timeline | null {
        animationConfig.scaling = animationConfig.scaling || {from : 1, to : 1};
        this._playSound = playSound;
        const countUpTween:TimelineLite = new TimelineLite();
        countUpTween.add(
            TweenLite.fromTo(this, animationConfig.duration,
                {
                    currentValue : fromValue,
                    scaling : animationConfig.scaling.from,
                },
                {
                    currentValue : toValue,
                    scaling : animationConfig.scaling.to,
                    ease : animationConfig.ease ? animationConfig.ease : Power3.easeInOut,
                    onStart : () => {
                        if(this._shrinkSize) {
                            this.fontSize = this._originalFontSize;
                            //this.updateText(); // removed in PIXI v5
                        }
                        this.onCountUpUpdate();
                        this.updateCountUpTextPivot();
                        this._isCountingUp = true;
                    },
                    onUpdate : () => this.onCountUpUpdate(),
                    onComplete : () => {
                        this.resetCountUp();
                        this.onCountUpUpdate();
                        if(onCompleteCallback) {
                            onCompleteCallback();
                        }
                    }
                })
        );

        return countUpTween;
    }

    private onCountUpUpdate():void {
        const lastStringLength:number = this.text.length;
        const newValue:number = Math.floor(this._currentValue);
        if (this._playSound == true){
            if(newValue > parseInt(this.text.slice(1, this.text.length))) {
                SlotGame.sound.playEffect(this._upgradeSound[this._currentUpgradeSound]);
                this._currentUpgradeSound++;
                if(this._currentUpgradeSound == this._upgradeSound.length) {
                    this._currentUpgradeSound = 0;
                }
            }
        }
        this.text = "x" + newValue.toString();
        this.scale.set(this.scaling, this.scaling);

        if(lastStringLength != this.text.length) {
            this.updateCountUpTextPivot();
            if(this._onWidthChangeCallback) {
                this._onWidthChangeCallback();
            }
        }

        // Play external callbacks.
        for(let i:number = 0; i < this._externalOnUpdateCallbacks.length; i++) {
            this._externalOnUpdateCallbacks[i]();
        }

        // Play passed unused anchors.
        if(this._highestTriggeredAnchorIndex < (this._anchors.length - 1)) {
            for(let i:number = this._highestTriggeredAnchorIndex + 1; i < this._anchors.length; i++) {
                if(this._anchors[i].value <= this._currentValue) {
                    this._anchors[i].callback();
                    if(this._anchors[i].oneTimeUse) {
                        this._anchors.splice(i, 1);
                        i--;
                    } else {
                        this._highestTriggeredAnchorIndex = i;
                    }
                }
            }
        }
    }

    private updateCountUpTextPivot():void {
        if(!this.parent) {
            this._isResizeDirty = true;
            return;
        }

        this.dirty = true;
        this.updateTransform(); //Force update transform so that the textWidth are updated.
        if(this._shrinkSize) {
            if(this._maxTextWidth == null || this._maxTextWidth <= 0) {
                this._maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH;
            }
            while(this.textWidth * this._scaling > this._maxTextWidth) {
                this.fontSize--;
                this.dirty = true;
                this.updateTransform();
            }
        }
        this.pivot.set(this.textWidth * 0.5, this.height * 0.5);
        this._isResizeDirty = false;
    }

    private resetCountUp():void {
        this._isCountingUp = false;
        this._highestTriggeredAnchorIndex = -1;
    }
}